import React, { useState, useEffect } from 'react';
import Modal, { PrimaryButton, SecondaryButton } from './Modal.jsx';

const fieldLabel = { fontSize: 11, textTransform: 'uppercase', color: '#9A9A95', letterSpacing: '0.04em', marginBottom: 6 };
const inputStyle = {
  width: '100%', fontSize: 13, color: '#1A1A1A',
  padding: '7px 9px', border: '1px solid #E0DFD9', borderRadius: 6, background: '#fff', outline: 'none'
};

function today() {
  return new Date().toISOString().slice(0, 10);
}

export default function MeetingSummaryModal({ open, onClose, projectId, summary, onSaved }) {
  const isEdit = !!summary;
  const [heldOn, setHeldOn] = useState(summary?.held_on || today());
  const [body, setBody]     = useState(summary?.body || '');
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);

  useEffect(() => {
    if (open) {
      setHeldOn(summary?.held_on || today());
      setBody(summary?.body || '');
      setError(null);
    }
  }, [open, summary]);

  async function submit() {
    if (!body.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const url    = isEdit ? `/meeting_summaries/${summary.id}` : `/projects/${projectId}/meeting_summaries`;
      const method = isEdit ? 'PATCH' : 'POST';
      const res = await fetch(url, {
        method,
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || '',
          'Accept':       'application/json'
        },
        body: JSON.stringify({ meeting_summary: { held_on: heldOn, body: body.trim() } })
      });

      if (res.status === 401) { window.location = '/session/new'; return; }
      if (res.status === 403) {
        setError("You don't have permission — only the author or a project manager can change this summary.");
        return;
      }
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error((errBody.errors && errBody.errors.join(', ')) || `Save failed (${res.status})`);
      }

      onSaved(await res.json());
      onClose();
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit meeting summary' : 'Log meeting summary'}
      footer={
        <>
          <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
          <PrimaryButton onClick={submit} disabled={saving || !body.trim()}>
            {saving ? 'Saving…' : (isEdit ? 'Save' : 'Log')}
          </PrimaryButton>
        </>
      }
    >
      <div className="mb-4">
        <div style={fieldLabel}>Meeting date</div>
        <input
          type="date"
          style={{ ...inputStyle, width: 'auto' }}
          value={heldOn}
          onChange={(e) => setHeldOn(e.target.value)}
        />
      </div>

      <div className="mb-4">
        <div style={fieldLabel}>Summary</div>
        <textarea
          style={{ ...inputStyle, minHeight: 220, resize: 'vertical', fontFamily: 'inherit' }}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Paste from your notetaker (Fathom / Otter / Gemini Notes) or type the highlights yourself."
        />
      </div>

      {error && (
        <div style={{ marginTop: 8, color: '#9B1F26', fontSize: 12 }}>{error}</div>
      )}
    </Modal>
  );
}
